// Recepción de transferencia por QR — /bodega/transferencias/recibir?token=X
// El responsable del pañol destino escanea el QR y firma con su PIN.
import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  PackageCheck, ArrowRight, CheckCircle2, XCircle, Loader2, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';

export default function TransferenciaRecibirPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setError('Token faltante'); setLoading(false); return; }
    // Usar fetch nativo (sin Authorization header) porque este endpoint es público
    // — si el operario abre el QR desde el teléfono sin sesión, no debe redirigir a login.
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/bodega/transferencias/publico/${token}`)
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.detail || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then((d) => setData(d))
      .catch((err) => setError(err.message || 'Token inválido'))
      .finally(() => setLoading(false));
  }, [token]);

  const confirmar = async () => {
    if (pin.length < 4) { toast.error('PIN inválido'); return; }
    setSubmitting(true);
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const r = await fetch(`${base}/api/bodega/transferencias/recibir-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, pin }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      setDone(j);
      toast.success(`¡Recibido! · ${j.items_movidos} herramientas movidas`);
    } catch (err) {
      toast.error(err.message || 'Error al confirmar');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  if (error) {
    return (
      <div className="p-6 max-w-lg mx-auto" data-testid="transferencia-recibir-page">
        <Card><CardContent className="p-6 text-center space-y-3">
          <XCircle className="w-14 h-14 mx-auto text-rose-500" />
          <h2 className="text-xl font-bold">No pudimos abrir esta transferencia</h2>
          <p className="text-slate-500" data-testid="error-message">{error}</p>
          <Button onClick={() => navigate('/bodega/transferencias')}>Volver</Button>
        </CardContent></Card>
      </div>
    );
  }

  if (done) {
    return (
      <div className="p-6 max-w-lg mx-auto" data-testid="transferencia-recibida-ok">
        <Card className="border-emerald-300">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
            <h2 className="text-2xl font-bold">¡Recepción confirmada!</h2>
            <p className="text-slate-600">
              <b>{done.items_movidos}</b> herramientas ahora están en <b>{data.panol_destino_nombre}</b>.
            </p>
            <p className="text-xs text-slate-400">Firmado por {done.firma_destino.nombre}</p>
            <Button onClick={() => navigate('/bodega/transferencias')}>Ver transferencias</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const yaRecibida = data.estado === 'recibida';

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto space-y-4" data-testid="transferencia-recibir-page">
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3">
            <PackageCheck className="w-10 h-10 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold">Recepción de Transferencia</h1>
              <p className="text-sm text-slate-500">Confirma los ítems y firma con tu PIN.</p>
            </div>
          </div>

          <div className="bg-slate-50 border rounded-md p-3 flex items-center justify-center gap-3">
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">Desde</div>
              <div className="font-bold">{data.panol_origen_nombre}</div>
              <div className="text-[10px] text-emerald-600 mt-1">✓ {data.firma_origen?.nombre || '—'}</div>
            </div>
            <ArrowRight className="w-6 h-6 text-slate-400" />
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">Hacia</div>
              <div className="font-bold">{data.panol_destino_nombre}</div>
              <div className="text-[10px] text-slate-400">Tú recibes</div>
            </div>
          </div>

          {yaRecibida && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm text-emerald-800" data-testid="ya-recibida-banner">
              ✓ Esta transferencia ya fue recibida.
            </div>
          )}

          <div>
            <div className="font-semibold mb-2 text-sm">
              Herramientas ({(data.items || []).length})
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {(data.items || []).map((it, i) => (
                <div key={`${it.codigo}-${i}`} className="p-2 border-b text-xs">
                  <div className="font-mono text-indigo-700">{it.codigo}</div>
                  <div className="text-slate-600">{it.descripcion}</div>
                </div>
              ))}
            </div>
          </div>

          {data.observaciones && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs">
              <b>Obs:</b> {data.observaciones}
            </div>
          )}

          {!yaRecibida && (
            <div className="space-y-3 pt-3 border-t">
              <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
                <ShieldCheck className="w-4 h-4" />
                Firma del responsable destino
              </div>
              {data.responsable_destino_nombre && (
                <p className="text-xs text-slate-500">
                  Esperando firma de <b>{data.responsable_destino_nombre}</b>.
                </p>
              )}
              <Input
                type="password" inputMode="numeric" maxLength={8}
                placeholder="Ingresa tu PIN (4-8 dígitos)"
                value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="text-lg text-center"
                data-testid="input-pin-recibir"
              />
              <Button
                size="lg" className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={confirmar} disabled={submitting || pin.length < 4}
                data-testid="btn-confirmar-recibir"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Confirmando...</>
                ) : (
                  <><CheckCircle2 className="w-5 h-5 mr-2" /> Confirmar recepción</>
                )}
              </Button>
              <p className="text-[11px] text-center text-slate-400">
                Al confirmar, las herramientas quedarán registradas en tu pañol.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
